import type { JpShippingAddress, ShippingAddress, WesternShippingAddress } from "./api";
import type { AppLocale } from "../i18n";

// Format a structured JP shipping address for display, following Japan Post
// ordering and the 様 honorific convention (UX-005): 〒postal → prefecture+city+
// town → building → 様-honored name → kana → phone. Returns lines for rendering
// (the caller joins with line breaks).
export function formatJpAddressLines(addr: JpShippingAddress, locale: AppLocale): string[] {
  const lines: string[] = [];
  const postal = addr.postalCode.startsWith("〒") ? addr.postalCode : `〒${addr.postalCode}`;
  lines.push(postal);
  lines.push([addr.prefecture, addr.city, addr.town].filter(Boolean).join(" "));
  if (addr.building) lines.push(addr.building);
  // Honorific: Japanese uses the 様 suffix; English keeps the plain name.
  lines.push(locale === "ja" ? `${addr.recipientName} 様` : addr.recipientName);
  if (addr.recipientNameKana) lines.push(addr.recipientNameKana);
  if (addr.phone) lines.push(addr.phone);
  return lines;
}

// Format a Western/structured address (non-JP): recipient → street (line1/line2)
// → "city, state postcode" → phone. The destination country is shown separately.
export function formatWesternAddressLines(addr: WesternShippingAddress): string[] {
  const lines: string[] = [];
  lines.push(addr.recipientName);
  lines.push(addr.line1);
  if (addr.line2) lines.push(addr.line2);
  const cityStateZip = [
    [addr.city, addr.state].filter(Boolean).join(", "),
    addr.postalCode
  ]
    .filter(Boolean)
    .join(" ");
  if (cityStateZip) lines.push(cityStateZip);
  if (addr.phone) lines.push(addr.phone);
  return lines;
}

// Dispatch on countryCode so each market renders in its own convention.
export function formatAddressLines(addr: ShippingAddress, locale: AppLocale): string[] {
  return "prefecture" in addr
    ? formatJpAddressLines(addr, locale)
    : formatWesternAddressLines(addr);
}
